/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache, useMutation, useQuery } from 'react-query';

import { Button, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { Namespaces } from '../../i18n';
import { OrganizationUserApiService } from '../../services/OrganizationUserApiService';
import { useTableStyles } from '../../styles/table';
import { ApiClientError } from '../../types/api/ApiClientError';
import { ApiClientResponseData } from '../../types/api/ApiClientResponse';
import { EntityId } from '../../types/api/EntityId';
import { UserToOrganizationInvitation } from '../../types/organization/UserToOrganizationInvitation';
import { UserToOrganizationRegistrationInvitation } from '../../types/organization/UserToOrganizationRegistrationInvitation';
import { AsyncReturnType } from '../../types/utils/AsyncReturnType';
import { fetchInvitation, fetchRegistrationInvitation } from '../../utils/queryFns';
import { reactToInviteUserToOrganization } from '../../utils/reactions';
import { useOrganizationPermissions } from '../../utils/useOrganizationPermissions';
import { AlignCenter } from '../AlignCenter/AlignCenter';
import { createInvitedListStyles } from './InvitedToOrganizationList.styles';

type InvitedListProps = {
  organizationId: EntityId;
};

export const InvitedToOrganizationList = memo<InvitedListProps>(props => {
  const { organizationId } = props;

  const tableClasses = useTableStyles();
  const classes = createInvitedListStyles();
  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const permissions = useOrganizationPermissions(organizationId);

  const invitationQuery = useQuery([fetchInvitation.key, { organizationId }], fetchInvitation.fn);
  const registrationInvitationQuery = useQuery(
    [fetchRegistrationInvitation.key, { organizationId }],
    fetchRegistrationInvitation.fn,
  );

  const invitations = useMemo(() => invitationQuery.data?.payload || [], [invitationQuery.data]);
  const registrationInvitations = useMemo(
    () => registrationInvitationQuery.data?.payload || [],
    [registrationInvitationQuery.data],
  );
  const isFetching = useMemo(
    () => [invitationQuery.status, registrationInvitationQuery.status].some(v => v === 'loading'),
    [invitationQuery.status, registrationInvitationQuery.status],
  );

  const [cancelInvite] = useMutation(OrganizationUserApiService.removeOrganizationInvitations, {
    onMutate: ({ organizationId, userId }) => {
      queryCache.cancelQueries([fetchInvitation.key, { organizationId }]);

      const queryData = queryCache.getQueryData<AsyncReturnType<typeof fetchInvitation.fn>>([
        fetchInvitation.key,
        { organizationId },
      ]);

      const previousInvites = queryData?.payload ?? [];
      const indexOf = previousInvites.findIndex(i => i.id === userId);
      if (indexOf > -1) {
        const preparedInvites = [...previousInvites];
        const inviteToDelete = preparedInvites.splice(indexOf, 1);
        queryCache.setQueryData([fetchInvitation.key, { organizationId }], {
          message: 'successful',
          payload: preparedInvites,
        } as ApiClientResponseData);

        return () => {
          const nextArray = [...preparedInvites];
          nextArray.splice(indexOf, 0, inviteToDelete[0]);
          queryCache.setQueryData([fetchInvitation.key, { organizationId }], {
            message: 'successful',
            payload: nextArray,
          } as ApiClientResponseData);
        };
      }
      return () => {};
    },
    onSettled: (data, error, { organizationId }) => {
      void reactToInviteUserToOrganization({
        organizationId,
      });
    },
    onError: (e, variables, rollback) => {
      const error = e as ApiClientError;
      console.error(error);
      (rollback as () => any)();
    },
  });

  const [cancelRegistrationInvite] = useMutation(OrganizationUserApiService.removeOrganizationRegistrationInvitations, {
    onMutate: ({ organizationId, email }) => {
      queryCache.cancelQueries([fetchRegistrationInvitation.key, { organizationId }]);

      const queryData = queryCache.getQueryData<AsyncReturnType<typeof fetchRegistrationInvitation.fn>>([
        fetchRegistrationInvitation.key,
        { organizationId },
      ]);

      const previousInvites = queryData?.payload ?? [];
      const indexOf = previousInvites.findIndex(i => i.email === email);
      if (indexOf > -1) {
        const preparedInvites = [...previousInvites];
        const inviteToDelete = preparedInvites.splice(indexOf, 1);
        queryCache.setQueryData([fetchRegistrationInvitation.key, { organizationId }], {
          message: 'successful',
          payload: preparedInvites,
        } as ApiClientResponseData);

        return () => {
          const nextArray = [...preparedInvites];
          nextArray.splice(indexOf, 0, inviteToDelete[0]);
          queryCache.setQueryData([fetchRegistrationInvitation.key, { organizationId }], {
            message: 'successful',
            payload: nextArray,
          } as ApiClientResponseData);
        };
      }
      return () => {};
    },
    onSettled: (data, error, { organizationId }) => {
      void reactToInviteUserToOrganization({
        organizationId,
      });
    },
    onError: (e, variables, rollback) => {
      const error = e as ApiClientError;
      console.error(error);
      (rollback as () => any)();
    },
  });

  const onCancelInvite = async (invite: UserToOrganizationInvitation) => {
    try {
      await cancelInvite({
        organizationId,
        userId: invite.id,
      });
    } catch (e) {
      /* empty */
    }
  };

  const onCancelRegistrationInvite = async (invitation: UserToOrganizationRegistrationInvitation) => {
    try {
      await cancelRegistrationInvite({
        organizationId,
        email: invitation.email,
      });
    } catch (e) {
      /* empty */
    }
  };

  return (
    <TableContainer>
      <Table aria-label="projects table" className={classes.root}>
        <TableHead>
          <TableRow>
            <TableCell
              classes={{
                root: cx(tableClasses.headerTableCell, classes.firstTableCell),
              }}
            >
              {t('management.members_list.title_invited')}
            </TableCell>
            <TableCell colSpan={2} classes={{ root: tableClasses.headerTableCell }}>
              {t('management.members_list.title_expires_in')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invitations.map(invitation => (
            <TableRow key={invitation.id}>
              <TableCell
                classes={{
                  root: cx(tableClasses.bodyTableCell, classes.firstTableCell),
                }}
              >
                <Typography component="p" variant="body2">
                  {invitation.firstName} {invitation.lastName}
                </Typography>
              </TableCell>
              <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                <Typography component="p" variant="body2">
                  {t('management.members_list.expires_status_permanent')}
                </Typography>
              </TableCell>
              <TableCell
                align={'right'}
                classes={{
                  root: cx(classes.cancelCell, tableClasses.bodyTableCell),
                }}
              >
                {permissions.edit_organization && (
                  <Button
                    onClick={() => onCancelInvite(invitation)}
                    classes={{ root: classes.cancelButton }}
                    disabled={false}
                    aria-label={t(`${Namespaces.Common}:label.cancel_invite`)}
                    data-cy="members-tab-cancel-invitation"
                  >
                    {t('management.members_list.cancel_invite')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {registrationInvitations.map(invitation => (
            <TableRow key={invitation.email}>
              <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                <Typography component="p" variant="body2">
                  {invitation.email}
                </Typography>
              </TableCell>
              <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                <Typography component="p" variant="body2">
                  {t(`${Namespaces.Common}:dates.from_now`, {
                    date: invitation.expiry,
                  })}
                </Typography>
              </TableCell>
              <TableCell
                align={'right'}
                classes={{
                  root: cx(classes.cancelCell, tableClasses.bodyTableCell),
                }}
              >
                {permissions.edit_organization && (
                  <Button
                    onClick={() => onCancelRegistrationInvite(invitation)}
                    classes={{ root: classes.cancelButton }}
                    disabled={false}
                    aria-label={t(`${Namespaces.Common}:label.cancel_registration_invite`)}
                    data-cy="members-tab-cancel-invitation"
                  >
                    {t('management.members_list.cancel_invite')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!isFetching && !invitations.length && !registrationInvitations.length && (
            <TableRow>
              <TableCell classes={{ root: tableClasses.bodyTableCell }} colSpan={3}>
                <AlignCenter>
                  <Typography component="p" variant="body2">
                    {t('management.members_list.no_invited_users')}
                  </Typography>
                </AlignCenter>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
